import { useEventWebsiteGuestQuery } from '@apps/guest/GuestSite.graphql.generated';
import { EventDesignPurpose } from '@graphql/generated-base';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useAuthUpgradeRefetch } from '@shared/hooks/useAuthUpgradeQuery';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { shouldUseEventDesignDraft } from '@shared/utils/eventDesignPurpose';
import { isInIframe } from '@shared/utils/isInIframe';
import { useIsGuestSiteSsrEnabled } from './useIsGuestSiteSsrEnabled';

type UseEventWebsiteGuestDataArgs = Readonly<{
  eventHandle: string;
}>;

const isPreviewing = isInIframe();

export const useEventWebsiteGuestData = (args: UseEventWebsiteGuestDataArgs) => {
  const { eventHandle } = args;
  const { isAdmin, hasIdentifiedUserOnce } = useEventUserRole();
  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const isWebsitePublishCapabilityEnabled = websitePublishCapabilityFeature.eventExposure.value;

  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();

  const shouldUseDraft = !isWebsitePublishCapabilityEnabled && shouldUseEventDesignDraft();
  const eventDesignPurpose = shouldUseDraft ? EventDesignPurpose.draft : EventDesignPurpose.live;

  const { data, loading, error, refetch } = useEventWebsiteGuestQuery({
    variables: {
      eventHandle,
      eventDesignPurpose,
      isAdminDashboard: isPreviewing && isAdmin,
    },
    batchMode: 'fast',
    // cache-and-network: serve cached SSR data immediately while useAuthUpgradeRefetch
    // fetches fresh authenticated data in the background. Avoids the flash of empty state
    // that cache-first + manual refetch would cause.
    fetchPolicy: isGuestSiteSsrEnabled ? 'cache-and-network' : 'cache-first',
    ssr: true,
    // When SSR public-first is enabled: fire immediately without waiting for auth.
    // During SSR, isAdmin is false (anonymous) so isAdminDashboard is false — correct public view.
    // SEO metadata (GuestSiteHead) requires this data even for unpublished events.
    //
    // When SSR public-first is disabled: preserve original behavior — wait for GetMe
    // to resolve (hasIdentifiedUserOnce) before firing.
    skip: !eventHandle || (!isGuestSiteSsrEnabled && !hasIdentifiedUserOnce),
  });

  // SSR public-first: After GetMe resolves client-side, refetch with auth token
  // to get authenticated data (activeSession, admin-gated fields, etc.).
  // The authLink in client.ts automatically attaches the token on refetch.
  useAuthUpgradeRefetch(isGuestSiteSsrEnabled ? refetch : undefined);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
